<template>
  <div 
    ref="appLayoutRootRef"
    class="app-layout" 
    :class="{ 'is-admin': isAdminRoute, 'is-login': isLoginPage, 'has-profile-bg': hasProfileBackground }"
    :style="layoutBackgroundStyle"
  >
    <!-- 背景层（在所有页面显示，登录/注册页面除外） -->
    <div 
      v-if="hasProfileBackground" 
      class="background-layer"
      :style="backgroundLayerStyle"
      ref="backgroundLayerRef"
    />
    <!-- 亮色模式默认背景（由 CSS data-theme 控制显示，避免依赖 composable 未就绪） -->
    <div
      v-if="!isLoginPage"
      class="light-default-bg"
      :style="lightDefaultBgStyle"
      aria-hidden="true"
    />

    <!-- 顶部导航（始终贴顶，不随后续加载内容下移） -->
    <TopNav
      v-if="!isLoginPage"
      :class="{ 'with-background': hasProfileBackground }"
    />
    <!-- 亮色模式固定导航占位：纯占位无背景，避免多出一块“容器” -->
    <div
      v-if="!isLoginPage"
      class="nav-fixed-spacer"
      aria-hidden="true"
    />

    <!-- 维护预告条：放在导航下方，避免加载后把整页顶下去 -->
    <transition name="maint-banner-fade">
      <div
        v-if="!isLoginPage && showPreviewBanner"
        class="maint-preview-banner"
      >
        <div class="maint-preview-inner">
          <div class="maint-preview-title">
            {{ maintenance?.title || '系统维护预告' }}
          </div>
          <div class="maint-preview-content">
            {{ maintenance?.content || '管理员正在准备系统更新，请提前保存当前操作。' }}
          </div>
          <div class="maint-preview-meta">
            <span>倒计时：{{ countdownText }}</span>
            <span v-if="maintenance?.estimatedRecoveryAt">预计恢复：{{ formatMaintDateTime(maintenance.estimatedRecoveryAt) }}</span>
            <span v-if="maintenance?.contactInfo">联系方式：{{ maintenance.contactInfo }}</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- 全站公告跑马灯（登录相关页以外显示） -->
    <SiteTicker
      v-if="!isLoginPage"
    />
    
    <div class="layout-container">
      <!-- 侧边栏（仅管理端，收起状态同步到 adminSidebarCollapsed 供主内容左移） -->
      <SideNav
        v-if="isAdminRoute && !isLoginPage"
        :class="{ 'with-background': hasProfileBackground }"
      />
      
      <!-- 主内容区（侧栏收起时左移，仅留出收起按钮宽度） -->
      <main
        class="main-content"
        :class="{
          'has-sidebar': isAdminRoute,
          'sidebar-collapsed': isAdminRoute && adminSidebarCollapsed,
          'with-background': hasProfileBackground,
          'main-content--kb-read': isKbReadRoute
        }"
      >
        <div
          class="main-inner"
          :class="{ 'main-inner--kb-read': isKbReadRoute }"
        >
          <router-view />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick, provide } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useRankStore } from '@/stores/rank'
import { useMaintenanceStore } from '@/stores/maintenance'
import { AUTH_PAGE_PATHS } from '@/config/constants'
import { getImageUrl } from '@/utils/imageUrl'
import { DEFAULT_BACKGROUND_IMAGE, preloadDefaultBackground } from '@/utils/preloadBackground'
import TopNav from './TopNav.vue'
import SideNav from './SideNav.vue'
import SiteTicker from './SiteTicker.vue'

const route = useRoute()
const authStore = useAuthStore()
const rankStore = useRankStore()
const maintenanceStore = useMaintenanceStore()
const backgroundLayerRef = ref(null)
const appLayoutRootRef = ref(null)

// 亮色模式默认背景图（public/image/light-default-bg.png）
const LIGHT_DEFAULT_BG_URL = '/image/light-default-bg.png'
const lightDefaultBgStyle = computed(() => ({
  backgroundImage: `url("${LIGHT_DEFAULT_BG_URL}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  opacity: 0.4
}))

const maintenance = computed(() => maintenanceStore.maintenance)
const isMaintenanceActive = computed(() => maintenanceStore.isMaintenanceActive)
const showPreviewBanner = computed(() => !!maintenance.value && !isMaintenanceActive.value)

const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
const formatMaintDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}
const countdownText = computed(() => {
  const sec = Math.max(0, Number(maintenanceStore.countdownSeconds) || 0)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}小时${m}分${s}秒`
  if (m > 0) return `${m}分${s}秒`
  return `${s}秒`
})

// 与 App.vue 一致：登录、忘记密码等全屏认证相关页（见 config/constants.js AUTH_PAGE_PATHS）
const isLoginPage = computed(() => AUTH_PAGE_PATHS.includes(route.path))

const isAdminRoute = computed(() => {
  return route.path.startsWith('/admin')
})

/** 知识库阅读页（专题/文章详情）需要更宽版心，便于目录与正文并排展示 */
const isKbReadRoute = computed(() => {
  return route.name === 'KbSeries' || route.name === 'KbArticle'
})

// 管理端侧栏收起状态，供 SideNav 写入、主内容区用于左移
const adminSidebarCollapsed = ref(false)
provide('adminSidebarCollapsed', adminSidebarCollapsed)

// 仅非空字符串视为“已设置”，避免后端返回 "" 或空格导致误判为有自定义背景（出现先显示默认再变白）
const profileBackgroundImage = computed(() => {
  const raw = authStore.user?.profileBackgroundImage ?? ''
  const s = (typeof raw === 'string' ? raw : '').trim()
  return s || null
})

const profileBackgroundGradientType = computed(() => {
  const raw = authStore.user?.backgroundGradientType ?? ''
  const s = (typeof raw === 'string' ? raw : '').trim()
  return s || null
})

// 未设置自定义背景时一律使用默认背景图（tower）；有 token 即显示默认（避免 user 未拉取完时空白）
const effectiveBackgroundImage = computed(() => {
  const hasImage = !!profileBackgroundImage.value
  if (hasImage) return profileBackgroundImage.value
  const inApp = !isLoginPage.value && (authStore.user || authStore.token)
  if (inApp) return DEFAULT_BACKGROUND_IMAGE
  return null
})

const hasProfileBackground = computed(() => {
  const hasImage = !!profileBackgroundImage.value
  const hasGradient = !!profileBackgroundGradientType.value
  const isLogin = isLoginPage.value
  const inApp = authStore.user || authStore.token
  const useDefault = inApp && !isLogin && !hasImage && !hasGradient
  return (hasImage || hasGradient || useDefault) && !isLogin
})

const profileBackgroundOpacity = computed(() => {
  return authStore.user?.profileBackgroundOpacity || 0.8
})


const layoutBackgroundStyle = computed(() => {
  // 不再在 app-layout 上设置背景，而是使用背景层
  return {}
})

const backgroundLayerStyle = computed(() => {
  const hasBg = hasProfileBackground.value
  const image = effectiveBackgroundImage.value
  const gradientType = profileBackgroundGradientType.value
  
  if (!hasBg) {
    return {}
  }
  
  const opacity = profileBackgroundOpacity.value
  
  // 如果是渐变背景（用户自定义的渐变）
  if (gradientType && gradientType.startsWith('GRADIENT_')) {
    const gradientClass = gradientType.replace('GRADIENT_', '').toLowerCase().replace(/_/g, '-')
    const style = {
      opacity: opacity
    }
    
    // 根据渐变类型设置背景
    if (gradientClass === 'rainbow') {
      style.background = 'linear-gradient(135deg, #FF0000 0%, #FF7F00 14%, #FFFF00 28%, #00FF00 42%, #0000FF 57%, #4B0082 71%, #9400D3 100%)'
      style.backgroundSize = '200% 200%'
      style.animation = 'gradient-shift 3s ease infinite'
    } else if (gradientClass === 'starry') {
      style.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
      style.position = 'relative'
    } else if (gradientClass === 'neon-pink') {
      style.background = 'linear-gradient(135deg, #FF1493 0%, #FF69B4 50%, #FF1493 100%)'
      style.backgroundSize = '200% 200%'
      style.animation = 'gradient-shift 2s ease infinite'
    } else if (gradientClass === 'neon') {
      style.background = 'linear-gradient(135deg, #0066FF 0%, #00FFFF 100%)'
    } else if (gradientClass === 'gold') {
      style.background = 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)'
      style.backgroundSize = '200% 200%'
      style.animation = 'gradient-shift 2s ease infinite'
    } else if (gradientClass === 'purple') {
      style.background = 'linear-gradient(135deg, #8B00FF 0%, #4B0082 50%, #8B00FF 100%)'
      style.backgroundSize = '200% 200%'
      style.animation = 'gradient-shift 2.5s ease infinite'
    } else if (gradientClass === 'neon-blue') {
      style.background = 'linear-gradient(135deg, #00CED1 0%, #1E90FF 50%, #00CED1 100%)'
      style.backgroundSize = '200% 200%'
      style.animation = 'gradient-shift 2s ease infinite'
    }
    
    return style
  }
  
  // 普通背景图片（用户自定义或未达黄金时的默认图）
  if (image && !image.startsWith('GRADIENT_')) {
    let imageUrl = image.startsWith('/') ? image : getImageUrl(image)
    // 含中文等非 ASCII 的路径需编码，否则部分环境无法加载
    if (/[^\x00-\x7F]/.test(imageUrl)) {
      imageUrl = encodeURI(imageUrl)
    }
    const style = {
      backgroundImage: `url("${imageUrl}")`, // 添加引号，确保 URL 中的特殊字符被正确处理
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed', // 固定背景，滚动时不动
      opacity: opacity
    }
    return style
  }
  
  return {}
})

watch(() => authStore.user, (user) => {
  if (user) {
    rankStore.ensureLoaded()
    preloadDefaultBackground()
  }
}, { immediate: true })

watch([hasProfileBackground, profileBackgroundImage, profileBackgroundGradientType], () => {
  nextTick(() => {})
}, { immediate: true })

onMounted(() => {
  if (authStore.user) {
    rankStore.ensureLoaded()
    preloadDefaultBackground()
  }
  nextTick(() => {
    // #region agent log
    const el = appLayoutRootRef.value
    const cs = el ? getComputedStyle(el) : null
    fetch('http://127.0.0.1:7243/ingest/55782c5e-938a-4e69-b48d-a9f67ab25a42', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '1b0f91' }, body: JSON.stringify({ sessionId: '1b0f91', runId: 'pre-fix', hypothesisId: 'H1', location: 'AppLayout.vue:onMounted', message: 'layout theme + computed bg', data: { theme: document.body?.getAttribute('data-theme') || null, hasProfileBg: !!hasProfileBackground.value, bgColor: cs?.backgroundColor || null, bgImage: cs?.backgroundImage?.slice?.(0, 80) || null, layoutVar: cs?.getPropertyValue('--layout-bg-solid')?.trim() || null }, timestamp: Date.now() }) }).catch(() => {})
    // #endregion
  })
})
</script>

<style scoped lang="scss">
.app-layout {
  min-height: 100vh;
  background: var(--color-bg-page);
  position: relative;
  
  &.is-login {
    background: var(--color-bg);
  }
  
  &.has-profile-bg {
    // 亮色模式下使用浅色背景，深色模式下透明
    background: var(--layout-bg-solid, transparent) !important;
    
    .main-content.with-background {
      background: var(--content-bg-solid, rgba(21, 29, 46, 0.75)) !important;
      backdrop-filter: var(--content-backdrop-filter, blur(8px)) !important;
      position: relative;
      z-index: 10;
      
      &:not(.has-sidebar) {
        border-radius: var(--radius-lg);
        margin: var(--spacing-lg) 0;
        padding: var(--spacing-xl);
      }
    }
    
    :deep(.el-card) {
      background: rgba(21, 29, 46, 0.85) !important;
      backdrop-filter: blur(6px) !important;
    }
    
    :deep(.el-card__body) {
      background: transparent !important;
    }
  }
}

/* 亮色主题下：主内容区域使用浅色底、取消圆角，避免整页呈椭圆容器感 */
body[data-theme='light'] .app-layout.has-profile-bg .main-content.with-background {
  background: var(--color-bg-page) !important;
  backdrop-filter: none !important;
  border-radius: 0 !important;
}
body[data-theme='light'] .app-layout .main-content.with-background:not(.has-sidebar) {
  border-radius: 0 !important;
}

/* 亮色主题下：撤销背景图模式对卡片的深色强制覆盖（:deep 穿透到子组件如 Profile 内的 el-card） */
body[data-theme='light'] .app-layout.has-profile-bg :deep(.el-card) {
  background: #ffffff !important;
  backdrop-filter: none !important;
}

body[data-theme='light'] .app-layout.has-profile-bg :deep(.el-card__body) {
  background: #ffffff !important;
}

.layout-container {
  display: flex;
  min-height: calc(100vh - 72px); // TopNav 高度是 72px
  position: relative;
  z-index: 10; // 提高 z-index，确保在背景层之上
  overflow-x: visible;
  overflow-y: visible;
  margin-top: 0; // 确保没有额外的上边距
}

.main-content {
  flex: 1;
  padding: var(--spacing-xl);
  width: 100%;
  position: relative;
  z-index: 1; // 确保在背景层之上
  overflow-x: auto;
  overflow-y: auto;
  min-height: auto; // 确保高度自适应

  // 管理端侧边栏为 fixed，不占流，主内容需预留左侧宽度避免被遮挡（创建分类/创建专题等按钮可点击）
  &.has-sidebar {
    margin-left: 240px;
    transition: margin-left 0.25s ease;
  }
  // 侧栏收起时主内容左移，仅留出收起按钮区域（约 52px）
  &.has-sidebar.sidebar-collapsed {
    margin-left: 52px;
  }
}

.main-inner {
  /* 用户端页面：统一版心宽度，并始终水平居中 */
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: var(--spacing-md);
  }

  /* 知识库专题/文章阅读页：占满主内容区全部宽度，并保证有高度供目录/正文计算 */
  &.main-inner--kb-read {
    max-width: none;
    width: 100%;
    min-height: 100%;
  }
}

/* 知识库阅读页：主内容区左右无 padding，目录与正文尽量占满整屏 */
.main-content.main-content--kb-read {
  padding-left: 0 !important;
  padding-right: 0 !important;
  --kb-read-outer-padding: 0;
}

/* 管理端页面：让主体区域填满 SideNav 右侧的可用宽度 */
.main-content.has-sidebar .main-inner {
  max-width: 100%;
  margin: 0;
}

/* 亮色模式：固定导航占位条，仅占高度、无背景无边框，避免多出一块“容器” */
.nav-fixed-spacer {
  height: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  pointer-events: none;
}

/* 亮色模式默认背景层：默认隐藏，仅亮色主题下由下方 body[data-theme='light'] 规则显示 */
.light-default-bg {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
}

/* 维护预告条（在 TopNav 下方，不顶动导航） */
.maint-preview-banner {
  position: sticky;
  top: 0;
  z-index: 900;
  width: 100%;
  padding: 8px 16px;
  background: linear-gradient(90deg, #fff8e1, #ffe3b3);
  border-bottom: 1px solid rgba(255, 136, 0, 0.25);
}

.maint-preview-inner {
  max-width: 1400px;
  margin: 0 auto;
}

.maint-preview-title {
  font-size: 16px;
  font-weight: 700;
  color: #8a4b00;
}

.maint-preview-content {
  margin-top: 4px;
  font-size: 14px;
  color: #7a3f00;
}

.maint-preview-meta {
  margin-top: 6px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #6b3a00;
}

.maint-banner-fade-enter-active,
.maint-banner-fade-leave-active {
  transition: all 0.25s ease;
}

.maint-banner-fade-enter-from,
.maint-banner-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>

<style lang="scss">
// 亮色模式：导航为 sticky，占位条保留在文档流中，与深色一致，确保可垂直滚动
body[data-theme='light'] .nav-fixed-spacer {
  display: block !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}
// 亮色模式：用 flex 让主内容区撑满导航下方整屏，避免底部大片留白
body[data-theme='light'] .app-layout {
  display: flex !important;
  flex-direction: column !important;
  min-height: 100vh !important;
}
body[data-theme='light'] .layout-container {
  flex: 1 1 0% !important; // 占满除导航、ticker 外的剩余空间
  min-height: 0 !important; // 便于 flex 正确计算高度
  padding-top: 0 !important;
}
body[data-theme='light'] .main-content {
  min-height: 100% !important; // 填满 layout-container
}

// 亮色模式：不显示背景图，保持纯色 #f6f8fa，更贴近 GitHub 简洁风格
body[data-theme='light'] .app-layout .light-default-bg {
  display: none !important;
}

// 亮色模式：确保主内容区域有正确的背景色，避免看起来空白
body[data-theme='light'] .app-layout .main-content {
  background-color: var(--color-bg-page, #f6f8fa) !important;
}

/* 亮色模式：覆盖 has-profile-bg 的背景变量，并直接铺底色（压过全局 transparent） */
body[data-theme='light'] .app-layout.has-profile-bg {
  --layout-bg-solid: #f6f8fa !important;
  --content-bg-solid: #f6f8fa !important;
  --content-backdrop-filter: none !important;
  background: var(--layout-bg-solid, #f6f8fa) !important;
}

// 背景层全局样式，确保能穿透所有组件
.app-layout.has-profile-bg .background-layer {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
  width: 100vw !important;
  height: 100vh !important;
  display: block !important;

  // 深色遮罩，压住背景图亮度但保留氛围
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at top, rgba(15, 23, 42, 0.7), transparent 55%),
      linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.72));
    pointer-events: none;
  }
}

// 亮色主题下：不再使用背景图片和深色遮罩，直接用浅色页面底色
body[data-theme='light'] .app-layout.has-profile-bg .background-layer {
  display: none !important;
}

// 深色模式：主布局透明，让背景层透出（亮色禁止 transparent，否则整页发白且内容对比异常）
body:not([data-theme='light']) .app-layout.has-profile-bg {
  background: transparent !important;
}

.app-layout.has-profile-bg {
  position: relative;
  
  // 确保 TopNav 始终在最上层
  > .top-nav {
    z-index: 1000 !important;
  }
  
  // 确保其他子元素都在背景层之上
  > *:not(.top-nav) {
    position: relative;
    z-index: 1;
  }
}

// 仅在管理端保留玻璃态卡片，用户端内容模块使用各自 token 控制底色
.app-layout.has-profile-bg.is-admin {
  .el-card {
    background: rgba(21, 29, 46, 0.85) !important;
    backdrop-filter: blur(6px) !important;
  }
  
  .el-card__body {
    background: transparent !important;
  }
  
  .main-content .el-card {
    background: rgba(21, 29, 46, 0.85) !important;
    backdrop-filter: blur(6px) !important;
  }
}

// 亮色主题下：主内容区域使用浅色底，不再叠深色玻璃态
body[data-theme='light'] .app-layout.has-profile-bg .main-content.with-background {
  background: var(--color-bg-page) !important;
  backdrop-filter: none !important;
}

// 渐变背景动画
@keyframes gradient-shift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

// 星空背景特效
.app-layout.has-profile-bg .background-layer[style*="starry"]::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(2px 2px at 20% 30%, white, transparent),
    radial-gradient(2px 2px at 60% 70%, white, transparent),
    radial-gradient(1px 1px at 50% 50%, white, transparent),
    radial-gradient(1px 1px at 80% 10%, white, transparent),
    radial-gradient(2px 2px at 30% 80%, white, transparent);
  background-size: 100% 100%;
  animation: twinkle 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes twinkle {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}
</style>
